import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';

const VersionHistory = ({
    versions,
    showMore,
    onToggleMore,
    scope,
    onScopeChange,
    scopeLabel,
    onRollback,
    formatDateTime,
    shortText,
    feedback
}) => {
    const displayedVersions = showMore ? versions : versions.slice(0, 5);
    return (
        <Stack spacing={2}>
            {feedback}
            <Alert severity="warning" variant="outlined">
                Rollback affects active config. Bundle Builder reads active config for non-TV categories and TV display metadata.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <Box>
                            <Typography variant="h6">Version History</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Showing {displayedVersions.length} of {versions.length} versions. Rollback creates a new active version and keeps history intact.
                            </Typography>
                        </Box>
                        <TextField
                            label="Rollback scope"
                            select
                            size="small"
                            value={scope}
                            onChange={(event) => onScopeChange(event.target.value)}
                            SelectProps={{ native: true }}
                            sx={{ minWidth: 220 }}
                        >
                            {['internet', 'homePhone', 'mobility', 'security', 'addons', 'tvDisplaySettings', 'emailShell'].map((item) => (
                                <option key={item} value={item}>{scopeLabel(item)}</option>
                            ))}
                        </TextField>
                    </Stack>
                    <List dense disablePadding>
                        {displayedVersions.length === 0 && (
                            <ListItem disableGutters><ListItemText primary="No config versions found." /></ListItem>
                        )}
                        {displayedVersions.map((version) => (
                            <ListItem
                                key={version.id}
                                disableGutters
                                divider
                                secondaryAction={version.status === 'archived' ? (
                                    <Button size="small" variant="outlined" onClick={() => onRollback(version, scope)}>
                                        Rollback {scopeLabel(scope)}
                                    </Button>
                                ) : null}
                            >
                                <ListItemText
                                    primary={(
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography variant="body2" fontWeight={700}>Version {version.versionNumber}</Typography>
                                            <Chip
                                                label={version.status === 'active' ? 'Active' : version.status === 'draft' ? 'Draft' : 'Archived'}
                                                size="small"
                                                color={version.status === 'active' ? 'success' : version.status === 'draft' ? 'primary' : 'default'}
                                                variant="outlined"
                                            />
                                        </Stack>
                                    )}
                                    secondary={`${shortText(version.changeSummary || 'No summary', 90)} | Created ${formatDateTime(version.createdAt)}${version.publishedAt ? ` | Published ${formatDateTime(version.publishedAt)}` : ''}`}
                                    primaryTypographyProps={{ component: 'div' }}
                                    secondaryTypographyProps={{ component: 'div' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                    {versions.length > 5 && (
                        <Button variant="outlined" size="small" onClick={onToggleMore} sx={{ alignSelf: 'flex-start' }}>
                            {showMore ? 'Show fewer versions' : `Show more versions (${versions.length - 5} older)`}
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Stack>
    );
};

export default VersionHistory;
